<template>
    <VAppBar id="app-bar" class="py-2 px-2" rounded="lg" color="transparent" flat>
        <VCard class="d-flex main-app-bar w-100" flat rounded="lg" color="transparent">

            <div class="background-blur" :style="computedBackgroundStyle">
            </div>

            <VContainer class="d-flex align-center" style="padding-top: 11px; padding-bottom: 11px">
                <ClientOnly>
                    <VBtn v-if="mdAndDown" class="mr-2" @click="link.navDrawer = !link.navDrawer" size="small" icon>
                        <VIcon size="x-large" :icon="route.path === '/browse' ? '$mdiFilterVariant' : '$menu'" />
                    </VBtn>
                </ClientOnly>

                <AppLogo />

                <VSpacer />

                <template  v-for="linkButton in linkItems" :key="linkButton.id">
                    <VBtn
                        :class="`text-capitalize hidden-md-and-down mr-1 ${linkButton.space} ${linkButton.font ? `font-weight-${linkButton.font}` : ''}`"
                        flat v-ripple="{ class: 'text-primary' }" :aria-label="linkButton.text"
                        :height="linkButton.height" :color="linkButton.colour" :rounded="linkButton.rounded"
                        :variant="linkButton.variant" :to="linkButton.scrollTo ? undefined : linkButton.to"
                        :text="linkButton.text" :prepend-icon="`$mdi${linkButton.icon}`" :active="linkButton.scrollTo
                            ? route.path === '/' && activeSection === linkButton.scrollTo
                            : undefined" @click="handleLinkClick(linkButton)">
                        <template #prepend>
                            <VIcon :icon="`$mdi${linkButton.icon}`" size="large" />
                        </template>
                    </VBtn>


                </template>

                <AppBarThemeToggle aria-label="Theme Toggle" class="mx-2" />

                <VBtn v-if="false" @click="link.themeDrawer = !link.themeDrawer" icon color="medium-emphasis"
                    aria-label="Theme Drawer" size="small">
                    <VIcon size="x-large" icon="$mdiTune" />
                </VBtn>
            </VContainer>
        </VCard>
    </VAppBar>
</template>

<script setup lang="ts">
import { useAppLink } from '@S/appLink'
import { useAppStore } from '@S/appStore'
import { useTheme, useDisplay } from 'vuetify'
import AppBarThemeToggle from '../Theme/ToggleTheme.vue'

type ButtonVariant =
    | 'flat'
    | 'text'
    | 'elevated'
    | 'outlined'
    | 'plain'
    | 'tonal'

type SubItem = {
    id: number
    disabled: boolean
    text: string
    icon: string
    subText: string
    to: string
    width?: string
}

type LinkItem = {
    id: number
    text: string
    icon: string
    menu: boolean
    to?: string
    scrollTo?: string
    homeSection?: boolean
    variant?: ButtonVariant
    colour?: string
    rounded?: string
    font?: string
    height?: string
    space?: string
    subItems?: SubItem[]
}

const { mdAndDown, mdAndUp } = useDisplay()
const route = useRoute()
const router = useRouter()

const link = useAppLink()
const store = useAppStore()
const theme = useTheme()

const baseLinkItems = computed<LinkItem[]>(() => [
    {
        id: 2,
        text: 'Transformations',
        scrollTo: 'transformations',
        homeSection: true,
        icon: 'ImageMultiple',
        menu: false,
    },
    {
        id: 3,
        text: 'Testimonials',
        scrollTo: 'testimonials',
        homeSection: true,
        icon: 'MessageText',
        menu: false,
    },
])

const linkItems = computed<LinkItem[]>(() => {
    const items: LinkItem[] = [...baseLinkItems.value]

    if (store.isAuthenticated) {
        items.push({
            id: 4,
            text: 'Members Area',
            to: '/dashboard',
            icon: 'AccountGroup',
            menu: false,
        },
            {
                id: 4,
                text: 'BOOK A CONSULTATION',
                font: 'bold',
                scrollTo: 'contact',
                icon: 'LightningBolt',
                colour: 'primary',
                space: 'ml-2',
                rounded: 'pill',
                height: '44',
                variant: theme.global.name.value === 'dark' ? 'tonal' : 'flat',
                menu: false,
            })
    } else {
        items.push({
            id: 5,
            text: 'Members Area',
            to: '/auth/login',
            icon: 'AccountGroup',
            menu: false,
        },
            {
                id: 4,
                text: 'BOOK A CONSULTATION',
                font: 'bold',
                scrollTo: 'contact',
                icon: 'LightningBolt',
                colour: 'primary',
                space: 'ml-2',
                rounded: 'pill',
                height: '44',
                variant: theme.global.name.value === 'dark' ? 'tonal' : 'flat',
                menu: false,
            })
    }

    return items
})

/*
 * Currently visible scroll section.
 *
 * This is used to give scroll-based navigation items the same active
 * appearance that Vuetify normally gives buttons using :to.
 */
const activeSection = ref<string | null>(null)

const isScrolled = ref(false)

const updateActiveSection = () => {
    /*
     * Scroll sections only exist on the homepage.
     * Nothing in this nav should be active on another route.
     */
    if (route.path !== '/') {
        activeSection.value = null
        return
    }

    const sections = linkItems.value
        .filter(item => item.scrollTo)
        .map(item => item.scrollTo as string)

    if (!sections.length) {
        activeSection.value = null
        return
    }

    const appBar = document.getElementById('app-bar')
    const appBarHeight = appBar?.offsetHeight ?? 0

    /*
     * Imagine a horizontal detection line just underneath
     * the app bar.
     *
     * A section is active only while that line is actually
     * inside the section.
     */
    const probeY = appBarHeight + 40

    let currentSection: string | null = null

    for (const id of sections) {
        const element = document.getElementById(id)

        if (!element) {
            continue
        }

        const rect = element.getBoundingClientRect()

        if (rect.top <= probeY && rect.bottom > probeY) {
            currentSection = id
            break
        }
    }

    /*
     * Contact/footer sections are often shorter than the viewport,
     * so force the final scroll section active at the very bottom.
     */
    const atBottom =
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 2

    if (atBottom) {
        const lastSection = sections.at(-1)

        if (lastSection) {
            currentSection = lastSection
        }
    }

    activeSection.value = currentSection
}

function handleScroll() {
    isScrolled.value = window.scrollY > 0
    updateActiveSection()
}

const handleResize = () => {
    updateActiveSection()
}

onMounted(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    window.addEventListener('resize', handleResize, { passive: true })

    nextTick(() => {
        handleScroll()
    })
})

onUnmounted(() => {
    window.removeEventListener('scroll', handleScroll)
    window.removeEventListener('resize', handleResize)
})

const computedBackgroundStyle = computed(() => {
    if (isScrolled.value) {
        return `
            background-color: rgba(
                ${theme.global.name.value === 'light' ? '255, 255, 255' : '18, 18, 18'},
                ${store.deviceType === 'Desktop' ? 0.85 : 1}
            );
            transition: background-color .15s ease, backdrop-filter .15s ease;
            ${store.deviceType === 'Desktop'
                ? 'backdrop-filter: blur(5px) saturate(150%); box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);'
                : ''
            }
        `
    }

    return `
        background-color: transparent;
        transition: background-color .15s ease, backdrop-filter .15s ease;
    `
})

const menuBackgroundStyle = computed(() => {
    return `
        background-color: rgba(
            ${theme.global.name.value === 'light' ? '255, 255, 255' : '18, 18, 18'},
            ${store.deviceType === 'Desktop' ? 0.95 : 1}
        );
        transition: background-color .15s ease, backdrop-filter .15s ease;
        ${store.deviceType === 'Desktop'
            ? 'backdrop-filter: blur(5px) saturate(250%); box-shadow: 0 4px 10px rgba(0, 0, 0, 0.1);'
            : ''
        }
    `
})

const waitForElement = (
    id: string,
    timeout = 3000
): Promise<HTMLElement | null> => {
    return new Promise(resolve => {
        const start = performance.now()

        const check = () => {
            const el = document.getElementById(id)

            if (el) {
                resolve(el)
                return
            }

            if (performance.now() - start >= timeout) {
                resolve(null)
                return
            }

            requestAnimationFrame(check)
        }

        check()
    })
}

const handleLinkClick = async (item: LinkItem) => {
    if (!item.scrollTo) {
        return
    }

    const target = item.scrollTo

    /*
     * Already on homepage — no hash needed.
     */
    if (route.path === '/') {
        scrollTo(target)
        return
    }

    /*
     * Navigate home using the hash so Nuxt can reliably
     * find and scroll to the section after the page mounts.
     */
    await navigateTo({
        path: '/',
        hash: `#${target}`,
    })

    activeSection.value = target

    /*
     * Wait until the router's smooth scroll has completed.
     */
    await waitForScrollEnd()

    /*
     * Remove #transformations / #testimonials / etc.
     * without reloading the page.
     */
    window.history.replaceState(
        window.history.state,
        '',
        window.location.pathname + window.location.search
    )
}

const scrollTo = (id: string) => {
    const el = document.getElementById(id)

    if (!el) {
        return
    }

    el.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
    })
}

const waitForScrollEnd = (): Promise<void> => {
    return new Promise(resolve => {
        let finished = false

        const finish = () => {
            if (finished) {
                return
            }

            finished = true

            window.removeEventListener('scrollend', finish)
            window.clearTimeout(timeout)

            resolve()
        }

        /*
         * Modern browsers fire scrollend once the smooth scroll completes.
         * Timeout is a fallback in case the event isn't supported/fired.
         */
        window.addEventListener('scrollend', finish, { once: true })

        const timeout = window.setTimeout(finish, 1500)
    })
}

</script>

<style scoped>
.background-blur {
    border-radius: 8px !important;
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: -1;
}

.main-app-bar {
    position: relative;
    z-index: 1;
}
</style>